import { BsPlus } from "react-icons/bs"

import optimizeImage from "@src/utilities/optimize-image"
import classNames from "@src/utilities/class-names"

interface ExcerptTitleLinkProps {
  isFeatured: boolean
  isNext: boolean
  readTimeInMinutes?: number
  title?: string
  urlPath?: string
}
function ExcerptTitleLink({
  isFeatured,
  isNext,
  readTimeInMinutes,
  title,
  urlPath,
}: ExcerptTitleLinkProps) {
  return (
    <a
      aria-label={title}
      className="color-black hover:text-orange transition-colors"
      href={urlPath}
      data-component="journal_excerpt"
    >
      {isFeatured || isNext ? (
        <span
          className={classNames(
            "flex items-start justify-start mb-8 mt-4 md:leading-normal",
            isFeatured ? "text-3xl md:text-5xl" : "",
            isNext ? "text-2xl md:text-4xl" : ""
          )}
        >
          <span>
            <BsPlus />
          </span>
          <span className="grow">
            <span className="block font-extrabold tracking-wider uppercase">
              {title}
            </span>
            {readTimeInMinutes && (
              <span className="block text-sm font-serif">
                {readTimeInMinutes} min read
              </span>
            )}
          </span>
        </span>
      ) : (
        <>
          <span className="block font-bold md:font-extrabold md:pt-2 uppercase md:text-2xl">
            {title}
          </span>
          <span className="block text-sm font-serif">
            {readTimeInMinutes} min read
          </span>
        </>
      )}
    </a>
  )
}

export interface JournalExcerptProps {
  excerpt: onyx.Types.JournalExcerpt
  featuredStampUrl?: string
  isFeatured?: boolean
  isNext?: boolean
}
export default function JournalExcerpt({
  excerpt,
  featuredStampUrl,
  isFeatured = false,
  isNext = false,
}: JournalExcerptProps) {
  return (
    <div
      className={classNames(
        "mx-auto relative pb-4 xl:pb-8 w-full",
        isFeatured ? "px-4" : "",
        isFeatured || isNext ? "max-w-screen-2xl md:flex md:mt-8" : ""
      )}
      data-featured={isFeatured || isNext}
    >
      <div className={classNames(isFeatured || isNext ? "md:basis-1/2" : "")}>
        <a
          aria-label={`Read ${excerpt.title}`}
          className="bg-cover bg-center block mb-2 before:content-[''] before:block before:pt-[100%] md:before:pt-[52.59259%] md:mb-0"
          href={excerpt.urlPath}
          title={`Read ${excerpt.title}`}
          style={{
            backgroundImage: `url(${optimizeImage(
              excerpt.featuredImageUrl,
              900
            )})`,
          }}
        />
      </div>
      <div
        className={classNames(
          isFeatured || isNext ? "md:p-4 md:basis-1/2 md:self-center" : ""
        )}
      >
        {featuredStampUrl && (
          <img
            className="hidden lg:block absolute right-16 top-0 w-20"
            src={optimizeImage(featuredStampUrl, 300)}
          />
        )}
        {isFeatured && !isNext ? (
          <h1>
            <ExcerptTitleLink
              isFeatured={isFeatured}
              isNext={false}
              readTimeInMinutes={excerpt.readTimeInMinutes}
              title={excerpt.title}
              urlPath={excerpt.urlPath}
            />
          </h1>
        ) : (
          <h2>
            <ExcerptTitleLink
              isFeatured={isFeatured}
              isNext={isNext}
              readTimeInMinutes={excerpt.readTimeInMinutes}
              title={excerpt.title}
              urlPath={excerpt.urlPath}
            />
          </h2>
        )}
      </div>
    </div>
  )
}
